import { addAlert } from '../../utilities/helpers'

import {
  getBusStopsForRoute,
  getRouteFareByOriginAndDestination,
  getTransportationRoutes,
  getTransportationProfileGeolocationMetadata
} from '../../services'

import { formatCurrency } from '@smarttransit/common'

let lastUsedTransportationProfile = null

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function getWeekdayName (weekday) {
  // API stores weekday as 0-6 (moment.js style): 0=Sunday, 1=Monday, ...
  const parsed = typeof weekday === 'string' ? parseInt(weekday, 10) : weekday
  return typeof parsed === 'number' && parsed >= 0 && parsed <= 6 ? WEEKDAY_LABELS[parsed] : ''
}

function getRouteTypeLabel (route) {
  if (!route) return ''
  if (route.isPrimary) return 'Primary'
  if (route.isPrimaryReturn) return 'Primary Return'

  if (route.weekday || route.weekday === 0) {
    const dayName = getWeekdayName(route.weekday)
    const base = route.isReturn ? 'Return' : ''
    return dayName ? `${dayName}${base ? ` (${base})` : ''}` : base
  }

  return ''
}

function getExpressLabel (route) {
  if (!route || !route.isExpressRoute) return ''
  // const flatRateFare = route.stRoute && route.stRoute.metadata ? route.stRoute.metadata.flatRateFare : 0

  // if (flatRateFare || flatRateFare === 0) {
  //   // If the route is marked express, but flatRateFare is 0, still surface it
  //   return flatRateFare > 0 ? 'Express (Flat Rate)' : 'Express'
  // }

  return 'Express'
}

function buildRouteDropdownLabel (route) {
  if (!route) return ''
  const baseLabel = (route.stRoute && route.stRoute.label) ? route.stRoute.label : (route.profileRouteMetadata && route.profileRouteMetadata.routeLabel ? route.profileRouteMetadata.routeLabel : '')
  const suffixes = []

  const routeTypeLabel = getRouteTypeLabel(route)
  if (routeTypeLabel) suffixes.push(routeTypeLabel)

  const expressLabel = getExpressLabel(route)
  if (expressLabel) suffixes.push(expressLabel)

  return suffixes.length ? `${baseLabel} - ${suffixes.join(' - ')}` : baseLabel
}

export default {
  name: 'search-route-fare',
  props: {
    onFareSearchResult: {
      required: true,
      type: Function
    },
    onCancelFareSearch: {
      type: Function
    },
    onSelectedBusRouteData: {
      type: Function
    },
    currencyPrefix: {
      type: String
    }
  },
  data () {
    return {
      isSearchingBus: false,
      isSearchingFare: false,
      busRoutes: [],
      busRoutesData: [],
      selectedBusRouteId: null,
      transportationProfile: null,
      busRouteBusStopsCache: {},
      originBusStops: [],
      destinationBusStops: [],
      originalOriginBusStops: [],
      originalDestinationBusStops: [],
      selectedOriginBusStop: null,
      selectedDestinationBusStop: null,
      lastBusStopIdFromMetadata: null,
      routeFare: null,
      flatRateFare: 0
    }
  },
  computed: {
    selectedBusRoute () {
      return this.busRoutes.find((o) => o.value === this.selectedBusRouteId)
    },
    flatRateFareLabel () {
      return formatCurrency(this.flatRateFare, this.$props.currencyPrefix)
    }
  },
  watch: {
    busRoutes (val) {
      if (val?.length) {
        (async () => {
          for (const busRoute of val) {
            if (!this.busRouteBusStopsCache[busRoute.value]) {
              try {
                this.busRouteBusStopsCache[busRoute.value] = await getBusStopsForRoute(busRoute.value)
              } catch (error) {
                console.error(error)
                addAlert({ message: error, type: 'error' })
                return
              }
            }
          }

          this.refreshOriginAndDestinationBusStops()
        })()
      }
    },
    selectedBusRoute (val) {
      this.flatRateFare = 0

      if (val?.value && this.busRoutesData?.length) {
        const profileRoute = this.busRoutesData.find((o) => o.id === val.value)
        this.flatRateFare = profileRoute?.isExpressRoute ? profileRoute.stRoute.metadata.flatRateFare : 0

        if (this.flatRateFare > 0) {
          this.refreshOriginAndDestinationBusStops()
        }
      }

      this.$props.onSelectedBusRouteData && this.$props.onSelectedBusRouteData({
        selectedBusRoute: val,
        isFlatRateFare: this.flatRateFare > 0,
        transportationProfile: this.transportationProfile,
        selectedOriginBusStop: this.selectedOriginBusStop,
        selectedDestinationBusStop: this.flatRateFare ? this.originalDestinationBusStops[this.originalDestinationBusStops.length - 1] : this.selectedDestinationBusStop
      })
    },
    selectedBusRouteId (val) {
      this.selectedOriginBusStop = null
      this.selectedDestinationBusStop = null
      this.refreshOriginAndDestinationBusStops(val)
    },
    selectedDestinationBusStop (val) {
      this.refreshOriginAndDestinationBusStops()

      this.$props.onSelectedBusRouteData && this.$props.onSelectedBusRouteData({
        selectedBusRoute: this.selectedBusRoute,
        isFlatRateFare: this.flatRateFare > 0,
        transportationProfile: this.transportationProfile,
        selectedOriginBusStop: this.selectedOriginBusStop,
        selectedDestinationBusStop: this.flatRateFare ? this.originalDestinationBusStops[this.originalDestinationBusStops.length - 1] : val
      })
    },
    selectedOriginBusStop (val) {
      this.refreshOriginAndDestinationBusStops()

      this.$props.onSelectedBusRouteData && this.$props.onSelectedBusRouteData({
        selectedBusRoute: this.selectedBusRoute,
        isFlatRateFare: this.flatRateFare > 0,
        transportationProfile: this.transportationProfile,
        selectedOriginBusStop: val,
        selectedDestinationBusStop: this.flatRateFare ? this.originalDestinationBusStops[this.originalDestinationBusStops.length - 1] : this.selectedDestinationBusStop
      })
    }
    // Stop searching/filtering is handled inside the shared bus-stop-selector component.
  },
  mounted () {
    this.transportationProfile = lastUsedTransportationProfile || null

    if (this.transportationProfile) {
      this.loadRoutesForTransportationProfile()
    }
  },
  methods: {
    refreshOriginAndDestinationBusStops (latestSelected) {
      const selectedBusRouteId = latestSelected || this.selectedBusRouteId

      if (selectedBusRouteId) {
        let busRouteBusStops = this.busRouteBusStopsCache[selectedBusRouteId]

        if (Array.isArray(busRouteBusStops)) {
          if (this.lastBusStopIdFromMetadata) {
            busRouteBusStops = busRouteBusStops.slice(busRouteBusStops.findIndex((o) => o.id === this.lastBusStopIdFromMetadata) + 1)
          }

          this.originBusStops = this.selectedDestinationBusStop ? busRouteBusStops.slice(0, busRouteBusStops.findIndex((o) => o.id === this.selectedDestinationBusStop.id)) : busRouteBusStops.slice()
          this.destinationBusStops = this.selectedOriginBusStop ? busRouteBusStops.slice(busRouteBusStops.findIndex((o) => o.id === this.selectedOriginBusStop.id) + 1) : busRouteBusStops.slice()
        }
      } else {
        this.originBusStops = []
        this.destinationBusStops = []
      }

      // Keep copies for flat-rate behavior (auto-select last stop as destination)
      this.originalOriginBusStops = this.originBusStops.slice()
      this.originalDestinationBusStops = this.destinationBusStops.slice()

      // Auto-select last stop as destination for flat-rate routes once stops are available
      // Origin (pickup) is user-selected, not auto-selected
      if (this.flatRateFare > 0 &&
        Array.isArray(this.originalDestinationBusStops) && this.originalDestinationBusStops.length) {
        // Only set destination if not already selected
        if (!this.selectedDestinationBusStop) {
          this.selectedDestinationBusStop = this.originalDestinationBusStops[this.originalDestinationBusStops.length - 1]
        }
      }
    },
    resetBusRouteState () {
      this.selectedBusRouteId = null
      this.busRoutes = []
      this.busRoutesData = []
      this.busRouteBusStopsCache = {}
      this.originBusStops = []
      this.destinationBusStops = []
      this.originalOriginBusStops = []
      this.originalDestinationBusStops = []
      this.selectedOriginBusStop = null
      this.selectedDestinationBusStop = null
      this.lastBusStopIdFromMetadata = null
      this.flatRateFare = 0
    },
    onSelectedBus ({ transportationProfile }) {
      if (!transportationProfile || !transportationProfile.id) {
        this.transportationProfile = null
        lastUsedTransportationProfile = null
        this.resetBusRouteState()
        return
      }

      this.transportationProfile = transportationProfile
      lastUsedTransportationProfile = transportationProfile
      this.loadRoutesForTransportationProfile()
    },
    async loadRoutesForTransportationProfile () {
      if (!this.transportationProfile || !this.transportationProfile.id) {
        return
      }

      try {
        this.isSearchingBus = true
        this.resetBusRouteState()

        const transportationProfileMetadata = await getTransportationProfileGeolocationMetadata({
          transportationProfileId: this.transportationProfile.id,
          shortcodeId: this.transportationProfile.shortcodeId
        })

        if (transportationProfileMetadata) {
          this.lastBusStopIdFromMetadata = transportationProfileMetadata.lastBusStopId
        }

        this.selectedBusRouteId = transportationProfileMetadata?.transportationProfileRouteId

        this.busRoutesData = await getTransportationRoutes({
          transportationProfileId: this.transportationProfile.id
        })

        if (this.busRoutesData?.length) {
          this.busRoutes = this.busRoutesData.map((o) => {
            return {
              text: buildRouteDropdownLabel(o),
              value: o.id
            }
          })
          this.selectedBusRouteId = this.selectedBusRouteId ? this.busRoutesData.find((o) => o.id === this.selectedBusRouteId)?.id : null
          this.selectedBusRouteId = this.selectedBusRouteId ? this.selectedBusRouteId : this.busRoutesData.find((o) => o.isPrimary)?.id || this.busRoutesData[0].id
        } else {
          this.busRoutes = []
        }
      } catch (error) {
        console.error(error)
        addAlert({ message: error, type: 'error' })
      } finally {
        this.isSearchingBus = false
      }
    },
    async searchFare () {
      if (this.flatRateFare) {
        if (!this.selectedOriginBusStop) {
          addAlert({ message: 'Please select a pickup stop', type: 'error' })
          return
        }
        this.$props.onFareSearchResult({ fare: this.flatRateFare })
      } else {
        if (!this.selectedOriginBusStop || !this.selectedDestinationBusStop) {
          addAlert({ message: 'Please select origin and destination bus stops', type: 'error' })
          return
        }

        try {
          this.isSearchingFare = true

          const routeFare = await getRouteFareByOriginAndDestination({
            transportationProfileRouteId: this.selectedBusRouteId,
            startLongitude: this.selectedOriginBusStop.longlat[0],
            startLatitude: this.selectedOriginBusStop.longlat[1],
            endLongitude: this.selectedDestinationBusStop.longlat[0],
            endLatitude: this.selectedDestinationBusStop.longlat[1],
            busStopIdFrom: this.selectedOriginBusStop.id,
            busStopIdTo: this.selectedDestinationBusStop.id
          })

          if (!routeFare) {
            throw new Error('Fare not found')
          }

          this.routeFare = routeFare
          this.$props.onFareSearchResult(routeFare)
        } catch (error) {
          console.error(error)
          this.$props.onFareSearchResult(null, error)
        } finally {
          this.isSearchingFare = false
        }
      }
    },
    cancelFareSearch () {
      this.routeFare = null
      this.$props.onCancelFareSearch && this.$props.onCancelFareSearch()
    }
  }
}
